import { useState, useEffect, useCallback, useMemo } from 'react';
import styles from './Formulario.module.css';
import Swal from 'sweetalert2';
import {useAuth} from '../../Auth/AuthContext'; 

const API_BASE_URL = `${process.env.REACT_APP_API_URL}/universidades`;

const EditarCarrera = ({ abrirModal, cerrarModal, carreraId, onCarreraEditada }) => {
    const [formData, setFormData] = useState({
        nombre: '',
        areaId: '',
    });
    const {getToken} = useAuth(); 
    const token = getToken(); 
    const [areas, setAreas] = useState([]);
    const [universidadesAsociadas, setUniversidadesAsociadas] = useState([]);
    const [universidadesDisponibles, setUniversidadesDisponibles] = useState([]);
    const [universidadSeleccionada, setUniversidadSeleccionada] = useState('');
    const [isClosing, setIsClosing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingAreas, setLoadingAreas] = useState(false);
    const [errores, setErrores] = useState({
        nombre: '',
        area: '',
    });

    const validators = useMemo(() => ({
        nombre: (nombre) => {
            if (!nombre || nombre.trim().length < 3) {
                return 'El nombre debe tener al menos 3 caracteres.';
            }
            if (nombre.trim().length > 255) {
                return 'El nombre no puede exceder 255 caracteres.';
            }
            return '';
        }
    }), []);

    const limpiarFormulario = useCallback(() => {
        setFormData({
            nombre: '',
            areaId: '',
        });
        setAreas([]);
        setUniversidadesAsociadas([]);
        setUniversidadesDisponibles([]);
        setUniversidadSeleccionada('');
        setErrores({
            nombre: '',
            area: '',
        });
    }, []);

    const realizarPeticion = useCallback(async (url, options = {}) => {
        try {
            const response = await fetch(url, options);
            if (!response.ok) {
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (error) {
            console.error('Error en petición:', error);
            throw error;
        }
    }, []);

    useEffect(() => {
        if (!abrirModal) return;

        const cargarAreas = async () => {
            setLoadingAreas(true);
            try {
                const data = await realizarPeticion(`${API_BASE_URL}/areas`);
                setAreas(data || []);
            } catch (error) {
                Swal.fire({
                    title: 'Error',
                    text: 'No se pudieron cargar las áreas disponibles.',
                    icon: 'error',
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#242b57',
                    customClass: { popup: 'popupbonito' }
                });
            } finally {
                setLoadingAreas(false);
            }
        };

        cargarAreas();
    }, [abrirModal, realizarPeticion]);

    useEffect(() => {
        if (!carreraId || !abrirModal) return;

        const cargarDatosCarrera = async () => {
            setLoading(true);
            try {
                const data = await realizarPeticion(`${process.env.REACT_APP_API_URL}/admin/obtener-carrera/${carreraId}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                setFormData({
                    nombre: data.carrera?.ca_nombre || '',
                    areaId: data.carrera?.ar_clave || '',
                });
                setUniversidadesAsociadas(data.universidades || []);
            } catch (error) {
                Swal.fire({
                    title: 'Error',
                    text: 'No se pudieron cargar los datos de la carrera.',
                    icon: 'error',
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#242b57',
                    customClass: { popup: 'popupbonito' }
                });
            } finally {
                setLoading(false);
            }
        };

        cargarDatosCarrera();
    }, [carreraId, abrirModal, realizarPeticion]);

    useEffect(() => {
        if (!abrirModal) return;

        const cargarUniversidades = async () => {
            try {
                const data = await realizarPeticion(`${API_BASE_URL}/obtener-universidades`);
                const universidadesTransformadas = data.map(uni => ({
                    un_clave: uni.id,
                    un_nombre: uni.name,
                    un_logo: uni.logo,
                    un_pagina: uni.contact?.website
                }));
                setUniversidadesDisponibles(universidadesTransformadas);
            } catch (error) {
                console.error('Error al obtener universidades:', error);
            }
        };

        cargarUniversidades();
    }, [abrirModal, realizarPeticion]);

    useEffect(() => {
        if (!abrirModal && !isClosing) {
            limpiarFormulario();
        }
    }, [abrirModal, isClosing, limpiarFormulario]);

    useEffect(() => {
        if (isClosing) {
            const timer = setTimeout(() => {
                cerrarModal();
                setIsClosing(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isClosing, cerrarModal]);

    const handleInputChange = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        if (field === 'nombre') {
            setErrores(prev => ({ ...prev, nombre: validators.nombre(value) }));
        } else if (field === 'areaId') {
            setErrores(prev => ({ ...prev, area: value ? '' : 'Debe seleccionar un área.' }));
        }
    }, [validators]);

    const agregarUniversidad = useCallback(() => {
        if (!universidadSeleccionada) {
            Swal.fire({
                title: 'Selecciona una universidad',
                text: 'Debes seleccionar una universidad para asociar.',
                icon: 'warning',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#242b57',
                customClass: { popup: 'popupbonito' }
            });
            return;
        }

        const universidad = universidadesDisponibles.find(u => 
            String(u.un_clave) === String(universidadSeleccionada)
        );
        
        if (!universidad) {
            Swal.fire({
                title: 'Error',
                text: `No se pudo encontrar la universidad seleccionada (ID: ${universidadSeleccionada}).`,
                icon: 'error',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#242b57',
                customClass: { popup: 'popupbonito' }
            });
            return;
        }

        const yaAsociada = universidadesAsociadas.some(u => 
            String(u.un_clave) === String(universidadSeleccionada)
        );
            
        if (yaAsociada) {
            Swal.fire({
                title: 'Universidad ya asociada',
                text: 'Esta universidad ya está asociada a la carrera.',
                icon: 'info',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#242b57',
                customClass: { popup: 'popupbonito' }
            });
            return;
        }

        setUniversidadesAsociadas(prev => [...prev, universidad]);
        setUniversidadSeleccionada('');
        
        Swal.fire({
            title: 'Universidad agregada',
            text: `${universidad.un_nombre} ha sido asociada a la carrera.`,
            icon: 'success',
            confirmButtonText: 'Continuar',
            confirmButtonColor: '#242b57',
            customClass: { popup: 'popupbonito' },
            timer: 2000,
            timerProgressBar: true
        });
    }, [universidadSeleccionada, universidadesDisponibles, universidadesAsociadas]);

    const eliminarUniversidad = useCallback((universidadClave) => {
        setUniversidadesAsociadas(prev => 
            prev.filter(u => u.un_clave !== universidadClave)
        );
    }, []);

    const handleOverlayClick = useCallback((e) => {
        if (e.target === e.currentTarget) {
            setIsClosing(true);
        }
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsClosing(true);
    }, []);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        
        const nuevosErrores = { nombre: '', area: '' };
        let hayErrores = false;

        const errorNombre = validators.nombre(formData.nombre);
        if (errorNombre) {
            nuevosErrores.nombre = errorNombre;
            hayErrores = true;
        }

        if (!formData.areaId) {
            nuevosErrores.area = 'Debe seleccionar un área.';
            hayErrores = true;
        }

        setErrores(nuevosErrores);

        if (hayErrores) return;

        const datosCarrera = {
            nombre: formData.nombre.trim(),
            areaId: parseInt(formData.areaId),
            universidades: universidadesAsociadas.map(u => u.un_clave)
        };

        try {
            await realizarPeticion(`${process.env.REACT_APP_API_URL}/admin/actualizar-carrera/${carreraId}`, {
                method: 'PUT',
                headers: {
                    'Authorization':  `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(datosCarrera),
            });

            Swal.fire({
                title: 'Carrera actualizada correctamente.',
                icon: 'success',
                confirmButtonText: 'Continuar',
                confirmButtonColor: '#242b57',
                customClass: { popup: 'popupbonito' }
            })

                if (onCarreraEditada) { 
                    await onCarreraEditada(); 
                }
                
                handleCloseModal();
        } catch (error) {
            const errorMessage = error.message || 'Ha ocurrido un error al actualizar la carrera.';
            Swal.fire({
                title: 'Error',
                text: errorMessage,
                icon: 'error',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#242b57',
                customClass: { popup: 'popupbonito' }
            });
        }
    }, [formData.nombre, formData.areaId, universidadesAsociadas, carreraId, validators, realizarPeticion, handleCloseModal]);

    const universidadesParaSeleccionar = useMemo(() => 
        universidadesDisponibles.filter(u => 
            !universidadesAsociadas.some(ua => ua.un_clave === u.un_clave)
        ), [universidadesDisponibles, universidadesAsociadas]
    );

    if (!abrirModal && !isClosing) return null;

    return (
        <div className={styles.modalAniadir} onClick={handleOverlayClick}>
            <div className={`${styles.modalAniadirCont} ${abrirModal && !isClosing ? styles.modalAniadirAnim : isClosing ? styles.modalFadeOut : ''}`}>
                <div className={styles.modalScrollableAniadir}>
                    <button onClick={handleCloseModal} type="button" className={styles.closeBtnAniadir}>
                        ×
                    </button>
                    <h2 className={styles.tituloAniadir}>Editar carrera</h2>
                    
                    {loading ? (
                        <div claXssName={styles.loading}>
                                <div className={styles.loadingSpinner}></div>
                                <p>Cargando datos...</p>
                        </div>
                    ) : (
                        <form className={styles.formUsuario} onSubmit={handleSubmit}>
                            <label htmlFor="nombre">
                                Nombre de la carrera: *
                                <input
                                    type="text"
                                    id="nombre"
                                    value={formData.nombre}
                                    placeholder="Ingresa el nombre de la carrera"
                                    onChange={(e) => handleInputChange('nombre', e.target.value)}
                                    required
                                    maxLength="255"
                                />
                            </label>
                            {errores.nombre && <p className={styles.mensajeErrorAniadir}>{errores.nombre}</p>}

                            <label htmlFor="area">
                                Área de estudio: *
                                {loadingAreas ? (
                                    <select disabled>
                                        <option>Cargando áreas...</option>
                                    </select>
                                ) : (
                                    <select
                                        id="area"
                                        value={formData.areaId}
                                        onChange={(e) => handleInputChange('areaId', e.target.value)}
                                        required
                                    >
                                        <option value="">Selecciona un área</option>
                                        {areas.map(area => (
                                            <option key={area.ar_clave} value={area.ar_clave}>
                                                {area.ar_nombre}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </label>
                            {errores.area && <p className={styles.mensajeErrorAniadir}>{errores.area}</p>}

                            <div className={styles.seccionTelefonos}>
                                <label>Universidades asociadas:</label>
                                {universidadesAsociadas.length > 0 ? (
                                    universidadesAsociadas.map(universidad => (
                                        <div key={universidad.un_clave} className={styles.inputGroup}>
                                            <div className={styles.inputWrapper}>
                                                <div className={styles.telefonoInput}>{universidad.un_nombre}</div>
                                                <button
                                                    type="button"
                                                    onClick={() => eliminarUniversidad(universidad.un_clave)}
                                                    className={styles.botonEliminar}
                                                    title="Eliminar asociación"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <p className={styles.helpText}>
                                        No hay universidades asociadas a esta carrera.
                                    </p>
                                )}
                            </div>

                            <div className={styles.seccionTelefonos}>
                                <label htmlFor="universidadSelect">
                                    Agregar universidad:
                                </label>
                                <div className={styles.inputWrapper}>
                                    <select
                                        id="universidadSelect"
                                        value={universidadSeleccionada}
                                        onChange={(e) => setUniversidadSeleccionada(e.target.value)}
                                    >
                                        <option value="">Selecciona una universidad</option>
                                        {universidadesParaSeleccionar.map(universidad => (
                                            <option key={universidad.un_clave} value={universidad.un_clave}>
                                                {universidad.un_nombre}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <button
                                    type="button"
                                    onClick={agregarUniversidad}
                                    className={`${styles.botonAgregar} ${!universidadSeleccionada ? styles.botonDeshabilitado : ''}`}
                                    disabled={!universidadSeleccionada}
                                >
                                    + Agregar
                                </button>
                            </div>

                            <button type="submit" className={styles.botonGuard}>
                                Actualizar Carrera
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EditarCarrera;